import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { cn } from "../utils";
import { SolidShape } from "../solid-shape";
import { SOLID_SHAPE_VARIANTS, type SolidShapeColor } from "../solid-shape/solid-shape.types";
import { overlineVariants } from "./overline.variants";
import type { OverlineProps } from "./overline.types";
const MARKER_COLORS: readonly SolidShapeColor[] = ["bright-blue", "light-green", "turquoise", "peach", "avocado", "sky-blue", "orange", "sea", "mint", "yellow", "cobalt", "ocean", "afternoon"];

/**
 * Deterministic FNV-1a-style hash → bucket index. Same input always returns
 * the same index, so the picked shape is stable across SSR + hydration and
 * doesn't change on re-renders.
 */
function hashToIndex(input: string, modulo: number): number {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = hash * 31 + input.charCodeAt(i) | 0;
  }
  return Math.abs(hash) % modulo;
}
function childrenAsString(children: React.ReactNode): string {
  return React.Children.toArray(children).map(child => typeof child === "string" ? child : "").join("");
}

/**
 * Behaviour notes:
 * - `icon` defaults to `true`: a deterministic SolidShape marker renders to
 *   the left of the text. Hash of the children string picks both the shape
 *   variant and the color, so the marker is stable across SSR/hydration.
 *   Pass `icon={false}` to suppress, or override with `iconVariant` /
 *   `iconColor`. `"white"` is excluded from the auto-pick color pool.
 * - When `as="p"` and `icon={true}`, the outer wrapper falls back to `<span>`
 *   because the marker renders a `<div>` and a `<p>` cannot contain block
 *   elements (browser auto-closes the `<p>`, causing hydration mismatch).
 * - `className` lands on the outer wrapper, not on the text `<span>`. Layout
 *   utilities like `justify-center` behave as expected; typography overrides
 *   may not cascade into the text node — use `variant` or compose directly.
 */
const Overline = React.forwardRef<HTMLElement, OverlineProps>((t0, ref) => {
  const $ = _c(35);
  let children;
  let className;
  let iconColor;
  let iconVariant;
  let props;
  let t1;
  let t2;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      as: t1,
      icon: t2,
      iconVariant,
      iconColor,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = iconColor;
    $[4] = iconVariant;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
    $[8] = variant;
  } else {
    children = $[1];
    className = $[2];
    iconColor = $[3];
    iconVariant = $[4];
    props = $[5];
    t1 = $[6];
    t2 = $[7];
    variant = $[8];
  }
  const Component = t1 === undefined ? "span" : t1;
  const icon = t2 === undefined ? true : t2;
  if (!icon) {
    const t3 = ref as React.Ref<never>;
    let t4;
    if ($[9] !== className || $[10] !== variant) {
      t4 = cn(overlineVariants({
        variant,
        className
      }));
      $[9] = className;
      $[10] = variant;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    let t5;
    if ($[12] !== Component || $[13] !== children || $[14] !== props || $[15] !== t3 || $[16] !== t4) {
      t5 = <Component ref={t3} data-slot="overline" className={t4} {...props}>{children}</Component>;
      $[12] = Component;
      $[13] = children;
      $[14] = props;
      $[15] = t3;
      $[16] = t4;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    return t5;
  }
  const content = childrenAsString(children);
  const resolvedVariant = iconVariant ?? SOLID_SHAPE_VARIANTS[hashToIndex(content, SOLID_SHAPE_VARIANTS.length)];
  const resolvedColor = iconColor ?? MARKER_COLORS[hashToIndex(content, MARKER_COLORS.length)];
  const Wrapper = Component === "p" ? "span" : Component;
  const t3 = ref as React.Ref<never>;
  let t4;
  if ($[18] !== className) {
    t4 = cn("flex items-center gap-2", className);
    $[18] = className;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== resolvedColor || $[21] !== resolvedVariant) {
    t5 = <SolidShape variant={resolvedVariant} color={resolvedColor} animated={false} className="size-3 shrink-0 [display:var(--overline-marker-display,block)]" />;
    $[20] = resolvedColor;
    $[21] = resolvedVariant;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== variant) {
    t6 = cn(overlineVariants({
      variant
    }));
    $[23] = variant;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== children || $[26] !== t6) {
    t7 = <span className={t6}>{children}</span>;
    $[25] = children;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== Wrapper || $[29] !== props || $[30] !== t3 || $[31] !== t4 || $[32] !== t5 || $[33] !== t7) {
    t8 = <Wrapper ref={t3} data-slot="overline" className={t4} {...props}>{t5}{t7}</Wrapper>;
    $[28] = Wrapper;
    $[29] = props;
    $[30] = t3;
    $[31] = t4;
    $[32] = t5;
    $[33] = t7;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  return t8;
});
Overline.displayName = "Overline";
export { Overline };